/**
 * Widget availability matrix by access level (Beta-1 plan P3-21, Tim C0813 "which widgets are
 * available to which level of user"). Pure module.
 *
 * Two inputs make the matrix:
 *   - each widget's own `minRole` (registry) — the floor its author set;
 *   - `ACCESS_OVERRIDES` below — the ONE place an admin raises a floor per widget without touching
 *     the widget file. The stricter of the two wins.
 * "Default on" per role comes from `dashboardDefaults.js` (ROLE_DEFAULT_LAYOUTS).
 *
 * Enforced by `isWidgetAllowed` (registry) → gallery listing, default layouts and layout loading
 * (`WidgetGrid` skips items the viewer may not see, so a copied admin layout renders nothing it
 * should not). UI-level gating: the data behind a widget is fenced by firestore.rules / scope.
 */
import { ROLE_HIERARCHY, roleRank } from '../../services/roleHierarchy.js';

/** widgetId → stored role key the viewer must meet (raises the widget's own minRole). */
export const ACCESS_OVERRIDES = Object.freeze({
  'recent-activity': 'team_lead', // organization-wide event feed — supervisors and up
  'oe-breakdown': 'team_lead', // element-level detail is a reviewer's tool; ICs get the quadrants
});

/** Roles in rank order, highest first. */
export const ACCESS_LEVELS = Object.freeze(Object.keys(ROLE_HIERARCHY).sort((a, b) => roleRank(b) - roleRank(a)));

/** The effective floor for a widget: the stricter of its minRole and the override. */
export function effectiveMinRole(widget, overrides = ACCESS_OVERRIDES) {
  const own = widget?.minRole || 'respondent';
  const over = overrides[widget?.id];
  if (!over) return own;
  return roleRank(over) > roleRank(own) ? over : own;
}

/** May a role see this widget under the matrix? (null role = unresolved = allowed, registry convention) */
export function roleMeetsWidget(role, widget, overrides = ACCESS_OVERRIDES) {
  if (role == null) return true;
  return roleRank(role) >= roleRank(effectiveMinRole(widget, overrides));
}

/**
 * Build the matrix for documentation / Help: one row per widget, one cell per role with
 * { available, defaultOn } — defaultOn from the role's default layout for `dashboardKey`.
 */
export function buildAccessMatrix(registry, { dashboardKey = null, defaultSpecs = () => [], overrides = ACCESS_OVERRIDES } = {}) {
  return registry
    .list()
    .filter((w) => !dashboardKey || !w.dashboards || w.dashboards.includes(dashboardKey))
    .map((w) => {
      const cells = {};
      for (const role of ACCESS_LEVELS) {
        const available = roleMeetsWidget(role, w, overrides);
        const defaultOn = available && defaultSpecs(dashboardKey, role).some((spec) => (spec.widgetId || spec) === w.id);
        cells[role] = { available, defaultOn };
      }
      return { id: w.id, title: w.title, category: w.category, minRole: effectiveMinRole(w, overrides), cells };
    });
}
